/**
 * Initiative Portfolio - typed BFF client.
 *
 * Mirrors packages/bff/src/portfolio (camelCase over the wire; the BFF maps
 * snake_case at its store boundary). Envelope convention: reads return a
 * named-key object which this layer unwraps; errors are `{ error }` and are
 * thrown as Error(message).
 */

import { bffFetch } from '@shared/api/bffFetch';

export type TShirtSize = 'xs' | 's' | 'm' | 'l' | 'xl';
export type InitiativeStatus =
  | 'idea' | 'proposed' | 'approved' | 'active' | 'paused' | 'delivered' | 'cancelled';
export type Health = 'green' | 'amber' | 'red';

export const T_SHIRT_SIZES: TShirtSize[] = ['xs', 's', 'm', 'l', 'xl'];
export const INITIATIVE_STATUSES: InitiativeStatus[] = [
  'idea', 'proposed', 'approved', 'active', 'paused', 'delivered', 'cancelled',
];
export const HEALTH_VALUES: Health[] = ['green', 'amber', 'red'];

export interface LookupRow {
  id: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  /** Priority only: the value new initiatives start on. */
  isDefault?: boolean;
}

/**
 * Admin-renamable display labels for the FIXED vocabularies. Stored values
 * (the Record keys) never change; only what users see does.
 */
export interface DisplayLabels {
  status: Record<InitiativeStatus, string>;
  health: Record<Health, string>;
  size: Record<TShirtSize, string>;
}

export interface PortfolioMeta {
  businessUnits: LookupRow[];
  strategicPillars: LookupRow[];
  programs: LookupRow[];
  tags: LookupRow[];
  /** Leadership's priority vocabulary, managed in Portfolio Settings. */
  priorities: LookupRow[];
  /** Decision impact areas, managed in Settings like priority (migration 021). */
  impactAreas: LookupRow[];
  labels: DisplayLabels;
  me: {
    oid: string;
    name: string;
    canAdmin: boolean;
    canViewSensitive: boolean;
    /** Portfolio.Leadership: the VELT review pages and the Leadership card. */
    canVelt: boolean;
    /**
     * Portfolio.Updater: may record an update on ANY initiative, not only the
     * ones they are named on. Drives whether the "add an update" button shows
     * on rows the caller does not own; the BFF is still the enforcer.
     */
    canUpdateAny: boolean;
    /**
     * This person is named on an initiative, owns an open risk, or owns an
     * open next step. Drives the "My work" nav section (ADO #1551), which is
     * data-driven rather than role-gated on purpose: nothing extra to grant,
     * and a pure leadership reader never sees the delivery surfaces.
     */
    hasDeliveryWork: boolean;
  };
  /** 'YYYY-MM-01' for the month we are in. */
  currentPeriod: string;
}

export type LookupKind =
  | 'businessUnit'
  | 'strategicPillar'
  | 'program'
  | 'tag'
  | 'priority'
  | 'impactArea';

export interface LookupPatch {
  name?: string;
  sortOrder?: number;
  isActive?: boolean;
  /** Priority only: promote this row to the default (exactly one holds it). */
  isDefault?: boolean;
}

export interface Initiative {
  id: string;
  /**
   * The short number people quote out loud (migration 019), rendered as #12.
   * Permanent and never reused. `id` remains the uuid every route uses.
   */
  ref: number;
  title: string;
  summary: string;
  description: string | null;
  businessUnitId: string;
  businessUnitName: string;
  strategicPillarId: string | null;
  strategicPillarName: string | null;
  tShirtSize: TShirtSize;
  status: InitiativeStatus;
  health: Health | null;
  percentComplete: number;
  accountablePersonId: string;
  accountablePersonName: string;
  deliveryLeadId: string | null;
  deliveryLeadName: string | null;
  productManagerId: string | null;
  productManagerName: string | null;
  programId: string | null;
  programName: string | null;
  /** Leadership's classification; set by admins and VELT members only. */
  priorityId: string | null;
  priorityName: string | null;
  prioritySortOrder: number | null;
  targetPeriod: string | null;
  startedOn: string | null;
  targetDate: string | null;
  completedOn: string | null;
  /** Catalog tag names (Portfolio Settings); renames propagate automatically. */
  tags: string[];
  isArchived: boolean;
  /** Visible only to Portfolio.Sensitive / admins / this row's owner+lead. */
  isSensitive: boolean;
  /** Requested for the VELT leadership agenda. Not a visibility gate. */
  isVelt: boolean;
  veltFlaggedById: string | null;
  veltFlaggedByName: string | null;
  veltFlaggedAt: string | null;
  openBlockerCount: number;
  latestSnapshotPeriod: string | null;
  /** When the newest update was recorded; a month can hold several. */
  latestUpdateAt: string | null;
  isStale: boolean;
  createdAt: string;
  /**
   * Who entered it (migration 015). Null for authors the backfill could not
   * name, and for rows created before the id itself was stamped.
   */
  createdById: string | null;
  createdByName: string | null;
  updatedAt: string;
}

export interface Snapshot {
  id: string;
  initiativeId: string;
  period: string;
  status: InitiativeStatus;
  health: Health | null;
  percentComplete: number;
  tShirtSize: TShirtSize;
  accountablePersonId: string;
  accountablePersonName: string;
  commentary: string | null;
  capturedAt: string;
  capturedById: string;
  capturedByName: string | null;
}

export interface Blocker {
  id: string;
  initiativeId: string;
  description: string;
  neededFromId: string | null;
  neededFromName: string | null;
  raisedOn: string;
  resolvedOn: string | null;
  isEscalated: boolean;
  /**
   * Who raised it (migration 016, ADO #1550). The id has been stamped since
   * 001; the name is denormalized beside it, so a blocker raised by someone
   * who has since left still reads as a person rather than a dead id. Null
   * when the author was never resolvable, which renders as the date alone.
   */
  raisedById: string | null;
  raisedByName: string | null;
}

export interface DashboardBlocker extends Blocker {
  initiativeTitle: string;
  accountablePersonName: string;
  daysOpen: number;
  /**
   * Enough of the parent initiative to record an update straight from the
   * blocker row: its current values, and the three people allowed to submit
   * one. Named to match Initiative so canRecordUpdate takes either shape.
   */
  initiativeStatus: InitiativeStatus;
  initiativeHealth: Health | null;
  initiativePercentComplete: number;
  accountablePersonId: string;
  deliveryLeadId: string | null;
  productManagerId: string | null;
}

/** A blocker read across the register (the Blockers page), with its context. */
export interface PortfolioBlocker extends DashboardBlocker {
  businessUnitName: string;
  programName: string | null;
}

// ── Decisions (the D of RAID, ADO #1495) ──

export type DecisionStatus = 'proposed' | 'decided' | 'superseded' | 'reversed';

export const DECISION_STATUSES: DecisionStatus[] = [
  'proposed',
  'decided',
  'superseded',
  'reversed',
];

/** One initiative a decision touches, as this reader is allowed to see it. */
export interface DecisionInitiativeRef {
  initiativeId: string;
  title: string;
  ref: number;
}

/**
 * The record of WHY. The only object in the module that can belong to zero,
 * one, or MANY initiatives: zero is a company decision, many is the big one
 * that governs a dozen projects at once.
 *
 * `initiatives` is filtered to what this reader may see, and `initiativeCount`
 * counts the same filtered set, never the true total.
 */
export interface Decision {
  id: string;
  title: string;
  decision: string;
  /** Why now, and what else was considered. One field on purpose. */
  context: string | null;
  decidedById: string | null;
  decidedByName: string | null;
  decidedOn: string | null;
  status: DecisionStatus;
  supersededById: string | null;
  supersededByTitle: string | null;
  impactAreaId: string | null;
  impactAreaName: string | null;
  tags: string[];
  externalUrl: string | null;
  isSensitive: boolean;
  createdAt: string;
  raisedById: string | null;
  raisedByName: string | null;
  initiatives: DecisionInitiativeRef[];
  initiativeCount: number;
}

export interface DecisionInput {
  title: string;
  decision: string;
  context?: string | null;
  decidedById?: string | null;
  decidedByName?: string | null;
  decidedOn?: string | null;
  status?: DecisionStatus;
  impactAreaId?: string | null;
  tags?: string[];
  externalUrl?: string | null;
  isSensitive?: boolean;
  /**
   * Zero, one, or many. OMIT on a patch to leave the associations alone;
   * sending an array replaces them wholesale.
   */
  initiativeIds?: string[];
}

// ── Risks (the R of RAID, ADO #1551) ──

export type RiskLevel = 'low' | 'medium' | 'high';
export type RiskResponse = 'avoid' | 'mitigate' | 'transfer' | 'accept';
export type RiskStatus = 'open' | 'closed' | 'occurred';
export type RiskSeverity = 'low' | 'medium' | 'high';

export const RISK_LEVELS: RiskLevel[] = ['low', 'medium', 'high'];
export const RISK_RESPONSES: RiskResponse[] = ['avoid', 'mitigate', 'transfer', 'accept'];

/**
 * A risk is what MIGHT happen; a blocker is what HAS happened.
 *
 * `score` is probability x impact on a 3x3 grid (1, 2, 3, 4, 6, 9), computed
 * by the database, and `severity` is the band it falls in, computed by the
 * BFF. Neither is re-derived here: one definition, in one place, so the page
 * and the email can never disagree about what counts as a high risk.
 */
export interface Risk {
  id: string;
  initiativeId: string;
  description: string;
  probability: RiskLevel;
  impact: RiskLevel;
  score: number;
  severity: RiskSeverity;
  /** Null is a real answer: nobody has decided what to do about it yet. */
  response: RiskResponse | null;
  mitigation: string | null;
  ownerId: string | null;
  ownerName: string | null;
  status: RiskStatus;
  raisedOn: string;
  reviewBy: string | null;
  closedOn: string | null;
  /** Set when the risk materialised and became this blocker. */
  becameBlockerId: string | null;
  raisedById: string | null;
  raisedByName: string | null;
}

/** A risk read across the register (the Risks page), with its context. */
export interface PortfolioRisk extends Risk {
  initiativeTitle: string;
  accountablePersonName: string;
  businessUnitName: string;
  programName: string | null;
  /** Named to match Initiative, so canRecordUpdate takes either shape. */
  accountablePersonId: string;
  deliveryLeadId: string | null;
  productManagerId: string | null;
  initiativeStatus: InitiativeStatus;
  initiativeHealth: Health | null;
  /** review_by has passed: nobody has re-rated this. */
  reviewOverdue: boolean;
}

export interface RiskCreateInput {
  description: string;
  probability: RiskLevel;
  impact: RiskLevel;
  response?: RiskResponse | null;
  mitigation?: string | null;
  ownerId?: string | null;
  ownerName?: string | null;
  reviewBy?: string | null;
  raisedOn?: string | null;
}

export interface RiskPatch {
  description?: string;
  probability?: RiskLevel;
  impact?: RiskLevel;
  response?: RiskResponse | null;
  mitigation?: string | null;
  ownerId?: string | null;
  ownerName?: string | null;
  reviewBy?: string | null;
  status?: RiskStatus;
  closedOn?: string | null;
}

// ── Next steps (the A of RAID, ADO #1551) ──

export type ActionStatus = 'open' | 'done' | 'dropped';

/**
 * One line, an owner, an optional due date, open/done/dropped. Deliberately
 * this thin and staying that way: the drift test is in the module guide.
 * Shared with the VELT layer, which writes the same table; `raisedAtVelt`
 * says leadership asked for it rather than the team writing it down.
 */
export interface ActionItem {
  id: string;
  initiativeId: string | null;
  agendaItemId: string | null;
  subjectTitle: string;
  meetingId: string | null;
  meetingDate: string | null;
  description: string;
  ownerId: string | null;
  ownerName: string | null;
  dueOn: string | null;
  status: ActionStatus;
  completedOn: string | null;
  createdAt: string;
  raisedAtVelt: boolean;
  createdByName: string | null;
}

export interface ActionCreateInput {
  description: string;
  ownerId?: string | null;
  ownerName?: string | null;
  dueOn?: string | null;
}

export interface ActionPatch {
  description?: string;
  ownerId?: string | null;
  ownerName?: string | null;
  dueOn?: string | null;
  status?: ActionStatus;
  completedOn?: string | null;
}

/** Everything owed by the person reading it, across every initiative. */
export interface MyWork {
  actions: ActionItem[];
  risks: PortfolioRisk[];
  blockers: PortfolioBlocker[];
  highScore: number;
}

export interface MyInitiative extends Initiative {
  /** Updates recorded this month; several is normal on a hot initiative. */
  updatesThisPeriod: number;
  currentPeriodCaptured: boolean;
  lastCommentary: string | null;
}

/**
 * One line of the dashboard's Recent updates feed: a snapshot with enough of
 * its initiative to read as news (many per month is normal, migration 013).
 */
export interface DashboardUpdate {
  id: string;
  initiativeId: string;
  initiativeTitle: string;
  businessUnitName: string;
  status: InitiativeStatus;
  health: Health | null;
  percentComplete: number;
  commentary: string | null;
  capturedAt: string;
  capturedByName: string | null;
}

export interface Dashboard {
  totals: { initiatives: number; active: number; openBlockers: number; stale: number };
  statusCounts: Record<string, number>;
  healthCounts: Record<string, number>;
  openBlockers: DashboardBlocker[];
  staleInitiatives: Initiative[];
  /** Flagged for the VELT agenda, with who asked (see Initiative.veltFlagged*). */
  veltRequests: Initiative[];
  /** Joined the register inside recentlyAddedWindowDays, newest first. */
  recentlyAdded: Initiative[];
  /** The window the server applied, so the page names the same one. */
  recentlyAddedWindowDays: number;
  /** Updates recorded inside recentUpdatesWindowDays, newest first, capped. */
  recentUpdates: DashboardUpdate[];
  /** The uncapped window count, so a capped list can say "25 of 41". */
  recentUpdatesTotal: number;
  recentUpdatesWindowDays: number;
  currentPeriod: string;
}

/**
 * A place the initiative points at: a SharePoint site or document, a Teams
 * channel, an ADO query. Replaced the single external_url field in migration
 * 008; `label` is optional and falls back to a readable form of the url.
 */
export interface InitiativeLink {
  id: string;
  initiativeId: string;
  url: string;
  label: string | null;
  note: string | null;
  sortOrder: number;
}

export interface LinkInput {
  url: string;
  label?: string | null;
  note?: string | null;
}

export interface LinkPatch extends Partial<LinkInput> {
  sortOrder?: number;
}

/**
 * An interested party on an initiative (migration 017, ADO #1498). Zero or
 * more per initiative, which is why they are their own rows rather than a
 * fourth person field: accountable / lead / PM are single-valued because
 * exactly one person answers for each.
 */
export interface Stakeholder {
  id: string;
  initiativeId: string;
  personId: string;
  personName: string;
  /** Why they are on it: their interest, not their job title. Optional. */
  why: string | null;
  sortOrder: number;
  addedByName: string | null;
}

export interface StakeholderInput {
  personId: string;
  personName: string;
  why?: string | null;
}

export interface StakeholderPatch {
  why?: string | null;
  personName?: string;
  sortOrder?: number;
}

export interface InitiativeDetail {
  initiative: Initiative;
  snapshots: Snapshot[];
  blockers: Blocker[];
  links: InitiativeLink[];
  stakeholders: Stakeholder[];
  /** Open first, then closed history (ADO #1551). */
  risks: Risk[];
  actions: ActionItem[];
  /** Includes company-spanning decisions that merely touch this one. */
  decisions: Decision[];
}

export interface InitiativeCreateInput {
  title: string;
  summary: string;
  description?: string | null;
  businessUnitId: string;
  strategicPillarId?: string | null;
  tShirtSize: TShirtSize;
  status: InitiativeStatus;
  health?: Health | null;
  percentComplete: number;
  accountablePersonId: string;
  accountablePersonName: string;
  deliveryLeadId?: string | null;
  deliveryLeadName?: string | null;
  productManagerId?: string | null;
  productManagerName?: string | null;
  programId?: string | null;
  priorityId?: string | null;
  targetPeriod?: string | null;
  startedOn?: string | null;
  targetDate?: string | null;
  completedOn?: string | null;
  tags?: string[];
  isSensitive?: boolean;
  /** Requested for VELT at creation; stamps the creator as the asker. */
  isVelt?: boolean;
  /** Create-time links. Afterwards links are their own endpoints. */
  links?: LinkInput[];
  /** Create-time stakeholders. Afterwards they are their own endpoints. */
  stakeholders?: StakeholderInput[];
}

/**
 * PATCH deliberately excludes links and stakeholders (each their own resource,
 * so an edit that forgets the array cannot silently empty the list) and isVelt
 * (its own route, so raising the flag never rides along with an unrelated edit).
 */
export type InitiativePatch = Partial<
  Omit<InitiativeCreateInput, 'links' | 'stakeholders' | 'isVelt'>
> & { isArchived?: boolean };

export interface SnapshotSubmitInput {
  status: InitiativeStatus;
  health: Health | null;
  percentComplete: number;
  commentary: string | null;
  targetPeriod?: string | null;
  targetDate?: string | null;
  completedOn?: string | null;
  /** Admin backfill only; omit for the normal current-month submit. */
  period?: string;
  /**
   * The VELT meeting this update is being recorded from. Lets a leadership
   * member push an update for an initiative on THAT meeting's agenda without
   * holding Portfolio.Updater; ignored for everyone else.
   */
  veltMeetingId?: string;
}

export interface BlockerCreateInput {
  description: string;
  neededFromId?: string | null;
  neededFromName?: string | null;
  isEscalated?: boolean;
  raisedOn?: string | null;
}

export interface BlockerPatch {
  description?: string;
  neededFromId?: string | null;
  neededFromName?: string | null;
  isEscalated?: boolean;
  resolvedOn?: string | null;
}

// ── Report subscriptions (the digest email) ──

export type DigestCadence = 'weekly' | 'monthly';

/** 'digest' = the changes email; 'list' = the register listing email. */
export type DigestReportKind = 'digest' | 'list';

/**
 * The digest's content scope: 'filters' tracks the user's saved register
 * filters (list.filters pref); 'picks' is an explicit slice. Empty arrays
 * mean no constraint on that axis.
 */
export interface SubscriptionScope {
  mode: 'filters' | 'picks';
  businessUnitIds?: string[];
  programIds?: string[];
  tags?: string[];
  onlyStale?: boolean;
  onlyOpenBlockers?: boolean;
}

export interface PortfolioSubscription {
  report: DigestReportKind;
  cadence: DigestCadence;
  scope: SubscriptionScope;
  lastSentAt: string | null;
  updatedAt: string;
}

export interface SubscriptionInput {
  cadence: DigestCadence;
  scope: SubscriptionScope;
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

async function readError(res: Response, fallback: string): Promise<never> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  throw new Error(body.error || `${fallback} (${res.status})`);
}

async function getJson<T>(path: string, fallback: string): Promise<T> {
  const res = await bffFetch(path);
  if (!res.ok) await readError(res, fallback);
  return (await res.json()) as T;
}

async function sendJson<T>(method: string, path: string, body: unknown, fallback: string): Promise<T> {
  const res = await bffFetch(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) await readError(res, fallback);
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// API surface
// ---------------------------------------------------------------------------

export const fetchMeta = () =>
  getJson<PortfolioMeta>('/api/portfolio/meta', 'Failed to load portfolio metadata');

export const fetchInitiatives = (includeArchived = false) =>
  getJson<{ initiatives: Initiative[] }>(
    `/api/portfolio/initiatives${includeArchived ? '?all=1' : ''}`,
    'Failed to load initiatives'
  ).then((r) => r.initiatives);

export const fetchInitiative = (id: string) =>
  getJson<InitiativeDetail>(`/api/portfolio/initiatives/${id}`, 'Failed to load the initiative');

export const createInitiative = (input: InitiativeCreateInput) =>
  sendJson<{ initiative: Initiative }>(
    'POST', '/api/portfolio/initiatives', input, 'Failed to create the initiative'
  ).then((r) => r.initiative);

export const updateInitiative = (id: string, patch: InitiativePatch) =>
  sendJson<{ initiative: Initiative }>(
    'PATCH', `/api/portfolio/initiatives/${id}`, patch, 'Failed to update the initiative'
  ).then((r) => r.initiative);

/**
 * "Add to VELT": raise or withdraw the request to discuss this initiative at
 * the leadership meeting. Any Portfolio user can raise one; only the asker, a
 * VELT member, or an admin can withdraw it.
 */
export const setVeltFlag = (id: string, isVelt: boolean) =>
  sendJson<{ initiative: Initiative }>(
    'PUT', `/api/portfolio/initiatives/${id}/velt`, { isVelt }, 'Failed to update the VELT flag'
  ).then((r) => r.initiative);

export const addLink = (initiativeId: string, input: LinkInput) =>
  sendJson<{ link: InitiativeLink }>(
    'POST', `/api/portfolio/initiatives/${initiativeId}/links`, input, 'Failed to add the link'
  ).then((r) => r.link);

export const updateLink = (linkId: string, patch: LinkPatch) =>
  sendJson<{ link: InitiativeLink }>(
    'PATCH', `/api/portfolio/links/${linkId}`, patch, 'Failed to update the link'
  ).then((r) => r.link);

export const deleteLink = async (linkId: string) => {
  const res = await bffFetch(`/api/portfolio/links/${linkId}`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Failed to delete the link');
};

export const addStakeholder = (initiativeId: string, input: StakeholderInput) =>
  sendJson<{ stakeholder: Stakeholder }>(
    'POST',
    `/api/portfolio/initiatives/${initiativeId}/stakeholders`,
    input,
    'Failed to add the stakeholder'
  ).then((r) => r.stakeholder);

export const updateStakeholder = (stakeholderId: string, patch: StakeholderPatch) =>
  sendJson<{ stakeholder: Stakeholder }>(
    'PATCH',
    `/api/portfolio/stakeholders/${stakeholderId}`,
    patch,
    'Failed to update the stakeholder'
  ).then((r) => r.stakeholder);

export const deleteStakeholder = async (stakeholderId: string) => {
  const res = await bffFetch(`/api/portfolio/stakeholders/${stakeholderId}`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Failed to remove the stakeholder');
};

export const submitSnapshot = (id: string, input: SnapshotSubmitInput) =>
  sendJson<{ ok: boolean; initiative: Initiative; snapshot: Snapshot }>(
    'POST', `/api/portfolio/initiatives/${id}/snapshot`, input, 'Failed to record the update'
  );

/**
 * Remove one update. Updates cannot be edited (they are append-only), so
 * this is the correction path for a mistake, open to its author or an admin
 * while the month is still open.
 */
export const deleteSnapshot = async (snapshotId: string) => {
  const res = await bffFetch(`/api/portfolio/snapshots/${snapshotId}`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Failed to remove the update');
};

export const createBlocker = (initiativeId: string, input: BlockerCreateInput) =>
  sendJson<{ blocker: Blocker }>(
    'POST', `/api/portfolio/initiatives/${initiativeId}/blockers`, input, 'Failed to raise the blocker'
  ).then((r) => r.blocker);

export const updateBlocker = (blockerId: string, patch: BlockerPatch) =>
  sendJson<{ blocker: Blocker }>(
    'PATCH', `/api/portfolio/blockers/${blockerId}`, patch, 'Failed to update the blocker'
  ).then((r) => r.blocker);

// ── Decisions ──

/** The whole log; history (superseded, reversed) only on request. */
export const fetchDecisions = (includeHistory = false) =>
  getJson<{ decisions: Decision[] }>(
    `/api/portfolio/decisions${includeHistory ? '?includeHistory=1' : ''}`,
    'Failed to load decisions'
  ).then((r) => r.decisions);

export const createDecision = (input: DecisionInput) =>
  sendJson<{ decision: Decision }>(
    'POST', '/api/portfolio/decisions', input, 'Failed to log the decision'
  ).then((r) => r.decision);

export const updateDecision = (decisionId: string, patch: DecisionInput | Partial<DecisionInput>) =>
  sendJson<{ decision: Decision }>(
    'PATCH', `/api/portfolio/decisions/${decisionId}`, patch, 'Failed to update the decision'
  ).then((r) => r.decision);

/**
 * Mark this decision replaced by that one. The old record is kept and pointed
 * at its replacement, never edited into it, so "why did we choose X, and what
 * changed" stays answerable.
 */
export const supersedeDecision = (decisionId: string, replacementId: string) =>
  sendJson<{ decision: Decision }>(
    'POST', `/api/portfolio/decisions/${decisionId}/supersede`, { replacementId },
    'Failed to supersede the decision'
  ).then((r) => r.decision);

export const deleteDecision = async (decisionId: string) => {
  const res = await bffFetch(`/api/portfolio/decisions/${decisionId}`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Failed to delete the decision');
};

export const createImpactArea = (name: string) =>
  sendJson<{ impactArea: LookupRow }>(
    'POST', '/api/portfolio/admin/impact-areas', { name }, 'Failed to create the impact area'
  ).then((r) => r.impactArea);

// ── Risks ──

export const createRisk = (initiativeId: string, input: RiskCreateInput) =>
  sendJson<{ risk: Risk }>(
    'POST', `/api/portfolio/initiatives/${initiativeId}/risks`, input, 'Failed to log the risk'
  ).then((r) => r.risk);

export const updateRisk = (riskId: string, patch: RiskPatch) =>
  sendJson<{ risk: Risk }>(
    'PATCH', `/api/portfolio/risks/${riskId}`, patch, 'Failed to update the risk'
  ).then((r) => r.risk);

/** Every risk the caller may see, highest exposure first. */
export const fetchRisks = (opts: { includeClosed?: boolean; highOnly?: boolean } = {}) => {
  const query = [
    opts.includeClosed ? 'includeClosed=1' : '',
    opts.highOnly ? 'highOnly=1' : '',
  ].filter(Boolean).join('&');
  return getJson<{ risks: PortfolioRisk[]; highScore: number }>(
    `/api/portfolio/risks${query ? `?${query}` : ''}`,
    'Failed to load risks'
  );
};

/**
 * "This happened": close the risk as occurred and raise the blocker it became,
 * linked, in one call. The server defaults the blocker's wording to the risk's
 * own, so the two cannot drift apart through retyping.
 */
export const promoteRisk = (
  riskId: string,
  input: {
    description?: string;
    neededFromId?: string | null;
    neededFromName?: string | null;
    isEscalated?: boolean;
  }
) =>
  sendJson<{ blockerId: string; risk: Risk }>(
    'POST', `/api/portfolio/risks/${riskId}/promote`, input,
    'Failed to record the risk as occurred'
  );

export const deleteRisk = async (riskId: string) => {
  const res = await bffFetch(`/api/portfolio/risks/${riskId}`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Failed to delete the risk');
};

// ── Next steps ──

export const createAction = (initiativeId: string, input: ActionCreateInput) =>
  sendJson<{ action: ActionItem }>(
    'POST', `/api/portfolio/initiatives/${initiativeId}/actions`, input,
    'Failed to add the next step'
  ).then((r) => r.action);

export const updateAction = (actionId: string, patch: ActionPatch) =>
  sendJson<{ action: ActionItem }>(
    'PATCH', `/api/portfolio/actions/${actionId}`, patch, 'Failed to update the next step'
  ).then((r) => r.action);

export const deleteAction = async (actionId: string) => {
  const res = await bffFetch(`/api/portfolio/actions/${actionId}`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Failed to remove the next step');
};

/** Next steps, risks, and blockers waiting on the caller. */
export const fetchMyWork = () =>
  getJson<MyWork>('/api/portfolio/my-work', 'Failed to load your work');

export const fetchMyInitiatives = () =>
  getJson<{ initiatives: MyInitiative[] }>('/api/portfolio/my', 'Failed to load your initiatives')
    .then((r) => r.initiatives);

/** Every blocker the caller may see, newest raised first. */
export const fetchBlockers = (includeResolved: boolean) =>
  getJson<{ blockers: PortfolioBlocker[] }>(
    `/api/portfolio/blockers${includeResolved ? '?includeResolved=1' : ''}`,
    'Failed to load blockers'
  ).then((r) => r.blockers);

export const fetchDashboard = () =>
  getJson<{ dashboard: Dashboard }>('/api/portfolio/dashboard', 'Failed to load the dashboard')
    .then((r) => r.dashboard);

export const createBusinessUnit = (name: string) =>
  sendJson<{ businessUnit: LookupRow }>(
    'POST', '/api/portfolio/admin/business-units', { name }, 'Failed to create the business unit'
  ).then((r) => r.businessUnit);

export const createStrategicPillar = (name: string) =>
  sendJson<{ strategicPillar: LookupRow }>(
    'POST', '/api/portfolio/admin/strategic-pillars', { name }, 'Failed to create the strategic pillar'
  ).then((r) => r.strategicPillar);

export const createProgram = (name: string) =>
  sendJson<{ program: LookupRow }>(
    'POST', '/api/portfolio/admin/programs', { name }, 'Failed to create the program'
  ).then((r) => r.program);

export const createTag = (name: string) =>
  sendJson<{ tag: LookupRow }>(
    'POST', '/api/portfolio/admin/tags', { name }, 'Failed to create the tag'
  ).then((r) => r.tag);

export const createPriority = (name: string) =>
  sendJson<{ priority: LookupRow }>(
    'POST', '/api/portfolio/admin/priorities', { name }, 'Failed to create the priority'
  ).then((r) => r.priority);

const LOOKUP_PATHS: Record<LookupKind, string> = {
  businessUnit: 'business-units',
  strategicPillar: 'strategic-pillars',
  program: 'programs',
  tag: 'tags',
  priority: 'priorities',
  impactArea: 'impact-areas',
};

export const createLookup = (kind: LookupKind, name: string) => {
  if (kind === 'businessUnit') return createBusinessUnit(name);
  if (kind === 'strategicPillar') return createStrategicPillar(name);
  if (kind === 'program') return createProgram(name);
  if (kind === 'priority') return createPriority(name);
  if (kind === 'impactArea') return createImpactArea(name);
  return createTag(name);
};

/** Set an initiative's priority on its own (leadership-gated route). */
export const setPriority = (id: string, priorityId: string | null) =>
  sendJson<{ initiative: Initiative }>(
    'PATCH', `/api/portfolio/initiatives/${id}`, { priorityId }, 'Failed to set the priority'
  ).then((r) => r.initiative);

export const updateLookup = (kind: LookupKind, id: string, patch: LookupPatch) =>
  sendJson<Record<string, LookupRow>>(
    'PATCH', `/api/portfolio/admin/${LOOKUP_PATHS[kind]}/${id}`, patch, 'Failed to update the list entry'
  ).then((r) => Object.values(r)[0]!);

export const fetchPref = <T,>(key: string) =>
  getJson<{ value: T | null }>(`/api/portfolio/prefs/${key}`, 'Failed to load preference')
    .then((r) => r.value);

export const savePref = (key: string, value: unknown) =>
  sendJson<{ ok: boolean }>(
    'PUT', `/api/portfolio/prefs/${key}`, { value }, 'Failed to save preference'
  );

/** Both of the caller's subscriptions (at most one per report type). */
export const fetchSubscriptions = () =>
  getJson<{ subscriptions: PortfolioSubscription[] }>(
    '/api/portfolio/subscriptions', 'Failed to load your subscriptions'
  ).then((r) => r.subscriptions);

export const saveSubscription = (report: DigestReportKind, input: SubscriptionInput) =>
  sendJson<{ subscription: PortfolioSubscription }>(
    'PUT', `/api/portfolio/subscriptions/${report}`, input, 'Failed to save your subscription'
  ).then((r) => r.subscription);

export const removeSubscription = async (report: DigestReportKind) => {
  const res = await bffFetch(`/api/portfolio/subscriptions/${report}`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Failed to remove your subscription');
};

/**
 * Fire one report email to the caller right now. Accepts an optional
 * {cadence, scope} override so a slice can be tried before subscribing;
 * without it the saved subscription is used.
 */
export const sendDigestNow = (report: DigestReportKind, input?: SubscriptionInput) =>
  sendJson<{ ok: boolean }>(
    'POST', `/api/portfolio/subscriptions/${report}/send-now`, input ?? {}, 'Failed to send the email'
  );

export const saveDisplayLabels = (labels: {
  status?: Record<string, string>;
  health?: Record<string, string>;
  size?: Record<string, string>;
}) =>
  sendJson<{ ok: boolean; labels: DisplayLabels }>(
    'PUT', '/api/portfolio/admin/labels', labels, 'Failed to save display labels'
  ).then((r) => r.labels);
